<template>
  <div ref="banner" class="cookies-notes" v-if="!isAccepted" :style="{'--stacked-above': bottomOffset}">
    <div class="cookies-notes--text">We use cookies to enhance your experience. By continuing, you consent to our use of cookies. <nuxt-link to="/privacy-policy">Learn More.</nuxt-link></div>
    <div class="cookies-notes--actions">
      <button class="btn-primary" @click="cookiesAccent">Accept</button>
      <button class="btn-close" @click="cookiesAccent">
        <span class="icon icon-close text-title-1"></span>
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
const isAccepted = ref(false);
const cookiesAccent = () => {
  localStorage.setItem('GDPR:accepted', 'true')
  isAccepted.value = true
  clearScrollPadding()
}

// While the banner is up it covers the bottom of the viewport. A form field that the browser
// scrolls into view on focus — or that a "first invalid field" scroll lands on — ends exactly
// under it: on the checkout the phone field (715px down on an 844px phone) sat beneath the
// banner until the visitor found Accept. scroll-padding-bottom on the root makes every
// focus/scrollIntoView stop above the banner instead, and costs nothing once it is dismissed.
const banner = ref<HTMLElement | null>(null)
function setScrollPadding() {
  if (typeof document === 'undefined' || isAccepted.value) return
  const height = banner.value?.getBoundingClientRect().height ?? 0
  const bottom = parseInt(bottomOffset.value, 10) || 0
  document.documentElement.style.scrollPaddingBottom = height ? `${Math.round(height + bottom + 8)}px` : ''
}
function clearScrollPadding() {
  if (typeof document === 'undefined') return
  document.documentElement.style.scrollPaddingBottom = ''
}

// The banner belongs at the bottom, but on phones the property page keeps a sticky Book footer
// there. Sitting ON it cost us every first-time mobile visitor: elementFromPoint at the centre of
// "Book Now" returned .cookies-notes and a real tap was swallowed. Moving the banner to the top
// fixed that and looked wrong. So it stays at the bottom and stacks ABOVE the footer, measured at
// runtime because the footer's height changes with the rate (161px without a resort fee, 246px
// with one). No footer on the page means the ordinary offset applies.
const FALLBACK_BOTTOM = 12
const bottomOffset = ref(`${FALLBACK_BOTTOM}px`)
let stopObserving: (() => void) | null = null

function measure() {
  if (typeof document === 'undefined') return
  const footer = document.querySelector('.sticky-wrapper') as HTMLElement | null
  if (!footer) { bottomOffset.value = `${FALLBACK_BOTTOM}px`; return }
  const rect = footer.getBoundingClientRect()
  // Only stack when the footer is genuinely pinned to the bottom of the screen, which is the
  // mobile case. On desktop the same wrapper is sticky near the top of a column and must not
  // push the banner anywhere.
  const pinnedToBottom = rect.height > 0 && Math.abs(rect.bottom - window.innerHeight) < 4
  bottomOffset.value = pinnedToBottom
    ? `${Math.round(rect.height) + FALLBACK_BOTTOM}px`
    : `${FALLBACK_BOTTOM}px`
  setScrollPadding()
}

onMounted(() => {
  isAccepted.value = localStorage.getItem('GDPR:accepted') ?  localStorage.getItem('GDPR:accepted') === 'true' : false
  if (isAccepted.value) return
  measure()
  nextTick(setScrollPadding)
  const footer = document.querySelector('.sticky-wrapper')
  const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => measure()) : null
  if (ro && footer) ro.observe(footer)
  if (ro && banner.value) ro.observe(banner.value)
  window.addEventListener('resize', measure, { passive: true })
  window.addEventListener('scroll', measure, { passive: true })
  stopObserving = () => {
    ro?.disconnect()
    window.removeEventListener('resize', measure)
    window.removeEventListener('scroll', measure)
  }
})

onBeforeUnmount(() => {
  stopObserving?.()
  clearScrollPadding()
})
</script>

<style lang="scss" scoped>
.cookies-notes {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: fixed;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  padding: 10px 16px;
  max-width: 1120px;
  margin: 0 auto;
  border: 1px solid #F8F8F8;
  background-color: var(--white);
  backdrop-filter: blur(7.5px);
  box-shadow: 0 2px 4px 0 #16161629;
  border-radius: 6px;
  z-index: 999;
  gap: 16px;

  @media screen and (max-width: 768px) {
    width: auto;
    max-width: 100%;
    left: 15px;
    right: 15px;
    transform: none;
    /* Stays at the bottom, stacked above the sticky Book footer rather than on top of it. The
       offset is measured at runtime (see the script block) because the footer's height depends on
       whether the rate carries a resort fee. Falls back to 12px when there is no pinned footer. */
    top: auto;
    bottom: var(--stacked-above, 12px);
  }

  button.btn-primary {
    background-color: var(--blue);
    height: 36px;
    border-radius: 6px;
    color: var(--white);
    font-size: 14px;
    display: flex;
    gap: 4px;
    padding: 10px 24px;
    justify-content: center;
    align-items: center;
    font-weight: 600;
  }

  .btn-close {
    span {
      font-size: 16px;
    }
  }


  &--text {
    color: #595757;

    a {
      color: var(--blue);
      font-weight: 600;
    }
  }

  &--actions {
    display: flex;
    gap: 12px;
  }
}
</style>
